import React, { useState, useEffect } from 'react';
import { Avatar, Typography, Box } from '@mui/material';
import { useUserData } from '../hooks/useUserData';

const UserDisplay = ({ email, userData: propUserData, size = 'medium', showName = true, variant = 'body2' }) => {
  const [userData, setUserData] = useState(propUserData || null);
  const { getUserData, getCachedUserData } = useUserData();

  useEffect(() => {
    // If userData is provided as prop, use it
    if (propUserData) {
      setUserData(propUserData);
      return;
    }

    // If no email, don't fetch
    if (!email) {
      setUserData(null);
      return;
    }

    // Check cache first
    const cachedData = getCachedUserData(email);
    if (cachedData) {
      setUserData(cachedData);
      return;
    }

    // Fetch user data if not in cache
    getUserData(email)
      .then(data => {
        setUserData(data);
      })
      .catch(error => {
        console.warn(`Error loading user data for ${email}:`, error);
        // Use fallback data
        setUserData({
          email: email,
          name: email.split('@')[0],
          photoUrl: null,
          initials: email.charAt(0).toUpperCase()
        });
      });
  }, [email, propUserData, getUserData, getCachedUserData]);

  if (!email || !userData) {
    return null;
  }

  const getAvatarSize = () => {
    switch (size) {
      case 'small': return 24;
      case 'large': return 48;
      default: return 32;
    }
  };

  const avatarSize = getAvatarSize();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar 
        src={userData.photoUrl}
        sx={{ 
          width: avatarSize, 
          height: avatarSize,
          bgcolor: userData.photoUrl ? 'transparent' : 'primary.main',
          fontSize: size === 'small' ? '0.75rem' : '1rem'
        }}
        onError={(e) => {
          // If photo fails to load, hide the src and show initials
          e.target.src = '';
          e.target.style.backgroundColor = 'primary.main';
        }}
      >
        {!userData.photoUrl && userData.initials}
      </Avatar>
      {showName && (
        <Typography variant={variant} color="textSecondary">
          {userData.name || email}
        </Typography>
      )}
    </Box>
  );
};

export default UserDisplay;
